import type { MapDefinition, PathCell } from '../../model/gamestate/GameState';
import { getPathTileTextureSync, type PathTileAtlasDefinition, type PathTileAtlasId } from '../pathTileRegistry';
import { resolveHexPathBrushMask } from './hexPathBrushCatalog';
import { legacyPathAutotiler } from './legacyPathAutotiler';
import type {
  NeighborRetilePatchEntry,
  PathAutotiler,
  PathTileNeighborStatus,
  PathTileRenderTile,
  PathTileRecommendation,
} from './PathAutotiler';
import { hasPathBorderContinuation } from './pathBorderContinuations';
import { decodeFixedPathVariantId } from './pathManualVariant';
import { findAtlasLayer, getPathAutotileProfile, isWithinMap } from './pathTileState';
import {
  composeSquarePathTileFromCornerMasks,
  resolveSquarePathBrushShape,
} from './squarePathProcedural';
import {
  composeTrianglePathTile,
  type TrianglePathFillCorner,
  resolveTrianglePathBrush,
  resolveTrianglePathBrushMask,
  resolveTriangleVariantMask,
  trianglePathAtlasTileIndex,
} from './trianglePathProcedural';

type Quadrant = 'northWest' | 'northEast' | 'southWest' | 'southEast';
type PathAutotileProfileId = 'square-path-v1' | 'hex-path-v1' | 'triangle-path-v1';
type PathLayer = ReturnType<typeof findAtlasLayer>;

interface PathAutotileProfile {
  readonly id: PathAutotileProfileId;
  getAffectedCoordinates(centerX: number, centerY: number): readonly { readonly x: number; readonly y: number }[];
  recommendAt(
    map: MapDefinition,
    atlasId: PathTileAtlasId,
    x: number,
    y: number,
    overrides?: ReadonlyMap<string, boolean>,
  ): PathTileRecommendation | null;
  resolveFixedRenderTile(
    map: MapDefinition,
    atlasId: PathTileAtlasId,
    x: number,
    y: number,
    cell: Extract<PathCell, { readonly mode: 'fixed' }>,
    atlas: PathTileAtlasDefinition,
    fixedTileIndex: number,
  ): PathTileRenderTile | null;
  resolveLiveRenderTile(
    map: MapDefinition,
    atlasId: PathTileAtlasId,
    x: number,
    y: number,
    cell: PathCell,
    atlas: PathTileAtlasDefinition,
  ): PathTileRenderTile | null;
}

const HEX_NEIGHBOR_OFFSETS_EVEN = [
  { x: 0, y: -1 },
  { x: 1, y: -1 },
  { x: 1, y: 0 },
  { x: 0, y: 1 },
  { x: -1, y: 0 },
  { x: -1, y: -1 },
] as const;

const HEX_NEIGHBOR_OFFSETS_ODD = [
  { x: 0, y: -1 },
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
  { x: -1, y: 1 },
  { x: -1, y: 0 },
] as const;

const squareRecommendationByCornerKey = buildSquareRecommendationLookup();

function encodeCornerMask(
  topLeft: boolean,
  topRight: boolean,
  bottomLeft: boolean,
  bottomRight: boolean,
): number {
  return Number(topLeft)
    | (Number(topRight) << 1)
    | (Number(bottomLeft) << 2)
    | (Number(bottomRight) << 3);
}

function buildSquareRecommendationLookup(): ReadonlyMap<string, PathTileRecommendation> {
  const lookup = new Map<string, PathTileRecommendation>();
  for (let mask = 0; mask < 256; mask += 1) {
    const signature = squareSignatureFromMask(mask);
    const key = squareCornerKeyFromNeighborStatus(signature);
    const recommendation = legacyPathAutotiler.recommendForNeighborStatus(signature);
    const existing = lookup.get(key);
    if (!existing) {
      lookup.set(key, recommendation);
      continue;
    }
    if (
      existing.tileIndex !== recommendation.tileIndex
      || existing.flipHorizontal !== recommendation.flipHorizontal
      || existing.flipVertical !== recommendation.flipVertical
      || existing.rotationSteps !== recommendation.rotationSteps
    ) {
      throw new Error(`Conflicting square autotile recommendations for corner key '${key}'.`);
    }
  }
  return lookup;
}

function squareSignatureFromMask(mask: number): PathTileNeighborStatus {
  return {
    north: (mask & (1 << 0)) !== 0,
    east: (mask & (1 << 1)) !== 0,
    south: (mask & (1 << 2)) !== 0,
    west: (mask & (1 << 3)) !== 0,
    northWest: (mask & (1 << 4)) !== 0,
    northEast: (mask & (1 << 5)) !== 0,
    southEast: (mask & (1 << 6)) !== 0,
    southWest: (mask & (1 << 7)) !== 0,
  };
}

function squareCornerKeyFromNeighborStatus(signature: PathTileNeighborStatus): string {
  const northWestMask = encodeCornerMask(signature.northWest, signature.north, signature.west, true);
  const northEastMask = encodeCornerMask(signature.north, signature.northEast, true, signature.east);
  const southWestMask = encodeCornerMask(signature.west, true, signature.southWest, signature.south);
  const southEastMask = encodeCornerMask(true, signature.east, signature.south, signature.southEast);
  return `${northWestMask}:${northEastMask}:${southWestMask}:${southEastMask}`;
}

function pathTileCellsEqual(left: PathCell | null, right: PathCell | null): boolean {
  if (!left || !right) {
    return left === right;
  }
  if (left.mode !== right.mode) {
    return false;
  }
  if ((left.surfaceTerrain ?? 'floor') !== (right.surfaceTerrain ?? 'floor')) {
    return false;
  }
  if (left.mode === 'autotile' || right.mode === 'autotile') {
    return true;
  }
  return left.variantId === right.variantId
    && (left.flipHorizontal === true) === (right.flipHorizontal === true)
    && (left.flipVertical === true) === (right.flipVertical === true)
    && (left.rotationSteps ?? 0) === (right.rotationSteps ?? 0);
}

function hasPathTileCell(cell: PathCell | null | undefined): boolean {
  return cell !== null && cell !== undefined;
}

function isLiveAutotileCell(cell: PathCell | null | undefined): boolean {
  return cell?.mode === 'autotile';
}

function isOccupied(
  map: MapDefinition,
  atlasId: PathTileAtlasId,
  layer: PathLayer,
  selfX: number,
  selfY: number,
  targetX: number,
  targetY: number,
  overrides?: ReadonlyMap<string, boolean>,
): boolean {
  const override = overrides?.get(`${targetX},${targetY}`);
  if (typeof override === 'boolean') {
    return override;
  }
  if (targetX === selfX && targetY === selfY) {
    return true;
  }
  if (!isWithinMap(map, targetX, targetY)) {
    return hasPathBorderContinuation(map.pathBorderContinuations, atlasId, targetX, targetY);
  }
  return hasPathTileCell(layer?.data[(targetY * map.width) + targetX] ?? null);
}

function squareNeighborStatus(
  map: MapDefinition,
  atlasId: PathTileAtlasId,
  x: number,
  y: number,
  overrides?: ReadonlyMap<string, boolean>,
): PathTileNeighborStatus | null {
  if (getPathAutotileProfile(map, atlasId) !== 'square-path-v1' || !isWithinMap(map, x, y)) {
    return null;
  }
  const layer = findAtlasLayer(map, atlasId);
  return {
    north: isOccupied(map, atlasId, layer, x, y, x, y - 1, overrides),
    east: isOccupied(map, atlasId, layer, x, y, x + 1, y, overrides),
    south: isOccupied(map, atlasId, layer, x, y, x, y + 1, overrides),
    west: isOccupied(map, atlasId, layer, x, y, x - 1, y, overrides),
    northWest: isOccupied(map, atlasId, layer, x, y, x - 1, y - 1, overrides),
    northEast: isOccupied(map, atlasId, layer, x, y, x + 1, y - 1, overrides),
    southEast: isOccupied(map, atlasId, layer, x, y, x + 1, y + 1, overrides),
    southWest: isOccupied(map, atlasId, layer, x, y, x - 1, y + 1, overrides),
  };
}

function squareRecommendForNeighborStatus(signature: PathTileNeighborStatus): PathTileRecommendation {
  const key = squareCornerKeyFromNeighborStatus(signature);
  return squareRecommendationByCornerKey.get(key) ?? legacyPathAutotiler.recommendForNeighborStatus(signature);
}

function squareCornerMasks(
  map: MapDefinition,
  atlasId: PathTileAtlasId,
  x: number,
  y: number,
): Record<Quadrant, number> | null {
  if (getPathAutotileProfile(map, atlasId) !== 'square-path-v1' || !isWithinMap(map, x, y)) {
    return null;
  }
  const layer = findAtlasLayer(map, atlasId);
  return {
    northWest: encodeCornerMask(
      isOccupied(map, atlasId, layer, x, y, x - 1, y - 1),
      isOccupied(map, atlasId, layer, x, y, x, y - 1),
      isOccupied(map, atlasId, layer, x, y, x - 1, y),
      true,
    ),
    northEast: encodeCornerMask(
      isOccupied(map, atlasId, layer, x, y, x, y - 1),
      isOccupied(map, atlasId, layer, x, y, x + 1, y - 1),
      true,
      isOccupied(map, atlasId, layer, x, y, x + 1, y),
    ),
    southWest: encodeCornerMask(
      isOccupied(map, atlasId, layer, x, y, x - 1, y),
      true,
      isOccupied(map, atlasId, layer, x, y, x - 1, y + 1),
      isOccupied(map, atlasId, layer, x, y, x, y + 1),
    ),
    southEast: encodeCornerMask(
      true,
      isOccupied(map, atlasId, layer, x, y, x + 1, y),
      isOccupied(map, atlasId, layer, x, y, x, y + 1),
      isOccupied(map, atlasId, layer, x, y, x + 1, y + 1),
    ),
  };
}

function hexNeighborOffsets(x: number): readonly { readonly x: number; readonly y: number }[] {
  return (x & 1) === 1 ? HEX_NEIGHBOR_OFFSETS_ODD : HEX_NEIGHBOR_OFFSETS_EVEN;
}

function buildHexNeighborMask(
  map: MapDefinition,
  atlasId: PathTileAtlasId,
  x: number,
  y: number,
  overrides?: ReadonlyMap<string, boolean>,
): number | null {
  if (getPathAutotileProfile(map, atlasId) !== 'hex-path-v1' || !isWithinMap(map, x, y)) {
    return null;
  }
  const layer = findAtlasLayer(map, atlasId);
  let mask = 0;
  const offsets = hexNeighborOffsets(x);
  for (let bit = 0; bit < offsets.length; bit += 1) {
    const offset = offsets[bit];
    if (isOccupied(map, atlasId, layer, x, y, x + offset.x, y + offset.y, overrides)) {
      mask |= (1 << bit);
    }
  }
  return mask;
}

function resolveHexTileRecommendation(mask: number): PathTileRecommendation {
  return {
    tileIndex: mask,
    flipHorizontal: false,
    flipVertical: false,
    rotationSteps: 0,
  };
}

function buildTriangleNeighborMask(
  map: MapDefinition,
  atlasId: PathTileAtlasId,
  x: number,
  y: number,
  overrides?: ReadonlyMap<string, boolean>,
): number | null {
  if (getPathAutotileProfile(map, atlasId) !== 'triangle-path-v1' || !isWithinMap(map, x, y)) {
    return null;
  }
  const layer = findAtlasLayer(map, atlasId);
  let mask = 0;
  const up = ((x + y) & 1) === 0;
  if (!up && isOccupied(map, atlasId, layer, x, y, x, y - 1, overrides)) {
    mask |= 1;
  }
  if (isOccupied(map, atlasId, layer, x, y, x + 1, y, overrides)) {
    mask |= 2;
  }
  if (up && isOccupied(map, atlasId, layer, x, y, x, y + 1, overrides)) {
    mask |= 4;
  }
  if (isOccupied(map, atlasId, layer, x, y, x - 1, y, overrides)) {
    mask |= 8;
  }
  return mask;
}

function resolveTriangleTileRecommendation(mask: number): PathTileRecommendation {
  return {
    tileIndex: mask,
    flipHorizontal: false,
    flipVertical: false,
    rotationSteps: 0,
  };
}

function buildTriangleRightHexagonCoords(x: number, y: number): readonly { readonly x: number; readonly y: number }[] {
  const up = ((x + y) & 1) === 0;
  const topRowY = up ? y : (y - 1);
  const bottomRowY = topRowY + 1;
  return [
    { x, y: topRowY },
    { x: x + 1, y: topRowY },
    { x: x + 2, y: topRowY },
    { x, y: bottomRowY },
    { x: x + 1, y: bottomRowY },
    { x: x + 2, y: bottomRowY },
  ];
}

function buildTriangleLeftHexagonCoords(x: number, y: number): readonly { readonly x: number; readonly y: number }[] {
  const up = ((x + y) & 1) === 0;
  const topRowY = up ? y : (y - 1);
  const bottomRowY = topRowY + 1;
  return [
    { x: x - 2, y: topRowY },
    { x: x - 1, y: topRowY },
    { x, y: topRowY },
    { x: x - 2, y: bottomRowY },
    { x: x - 1, y: bottomRowY },
    { x, y: bottomRowY },
  ];
}

function buildTriangleApexHexagonCoords(x: number, y: number): readonly { readonly x: number; readonly y: number }[] {
  const up = ((x + y) & 1) === 0;
  const topRowY = up ? (y - 1) : y;
  const bottomRowY = topRowY + 1;
  return [
    { x: x - 1, y: topRowY },
    { x, y: topRowY },
    { x: x + 1, y: topRowY },
    { x: x - 1, y: bottomRowY },
    { x, y: bottomRowY },
    { x: x + 1, y: bottomRowY },
  ];
}

function areTriangleCellsOccupied(
  map: MapDefinition,
  atlasId: PathTileAtlasId,
  coords: readonly { readonly x: number; readonly y: number }[],
): boolean {
  const layer = findAtlasLayer(map, atlasId);
  return coords.every(({ x, y }) =>
    isWithinMap(map, x, y)
    && layer?.data[(y * map.width) + x] !== null
  );
}

function resolveTriangleHexagonFillCorners(
  map: MapDefinition,
  atlasId: PathTileAtlasId,
  x: number,
  y: number,
  mask: number,
): readonly TrianglePathFillCorner[] {
  const up = ((x + y) & 1) === 0;
  const corners: TrianglePathFillCorner[] = [];
  const rightPairPresent = up ? ((mask & 2) !== 0 && (mask & 4) !== 0) : ((mask & 1) !== 0 && (mask & 2) !== 0);
  const leftPairPresent = up ? ((mask & 4) !== 0 && (mask & 8) !== 0) : ((mask & 8) !== 0 && (mask & 1) !== 0);
  const apexPairPresent = (mask & 2) !== 0 && (mask & 8) !== 0;
  if (rightPairPresent && areTriangleCellsOccupied(map, atlasId, buildTriangleRightHexagonCoords(x, y))) {
    corners.push('right');
  }
  if (leftPairPresent && areTriangleCellsOccupied(map, atlasId, buildTriangleLeftHexagonCoords(x, y))) {
    corners.push('left');
  }
  if (apexPairPresent && areTriangleCellsOccupied(map, atlasId, buildTriangleApexHexagonCoords(x, y))) {
    corners.push('apex');
  }
  return corners;
}

function resolveRenderTileWithProfile(
  profile: PathAutotileProfile,
  map: MapDefinition,
  atlasId: PathTileAtlasId,
  x: number,
  y: number,
  cell: PathCell | null,
  atlas: PathTileAtlasDefinition,
): PathTileRenderTile | null {
  if (!cell || getPathAutotileProfile(map, atlasId) !== profile.id || !isWithinMap(map, x, y)) {
    return null;
  }
  if (cell.mode === 'fixed') {
    const fixedVariant = decodeFixedPathVariantId(map.gridType, cell.variantId);
    return fixedVariant
      ? profile.resolveFixedRenderTile(map, atlasId, x, y, cell, atlas, fixedVariant.brushIndex)
      : null;
  }
  return profile.resolveLiveRenderTile(map, atlasId, x, y, cell, atlas);
}

const squarePathProfile: PathAutotileProfile = {
  id: 'square-path-v1',
  getAffectedCoordinates(centerX, centerY) {
    const coords: { x: number; y: number }[] = [];
    for (let y = centerY - 1; y <= centerY + 1; y += 1) {
      for (let x = centerX - 1; x <= centerX + 1; x += 1) {
        coords.push({ x, y });
      }
    }
    return coords;
  },
  recommendAt(map, atlasId, x, y, overrides) {
    const signature = squareNeighborStatus(map, atlasId, x, y, overrides);
    return signature ? squareRecommendForNeighborStatus(signature) : null;
  },
  resolveFixedRenderTile(_map, atlasId, _x, _y, _cell, atlas, fixedTileIndex) {
    const brushShape = resolveSquarePathBrushShape(fixedTileIndex);
    if (!brushShape) {
      return null;
    }
    const textureImage = getPathTileTextureSync(atlasId, 'square');
    return {
      kind: 'image',
      image: composeSquarePathTileFromCornerMasks(atlasId, atlas.tileSize * 2, {
        northWest: encodeCornerMask(brushShape.northWest, brushShape.north, brushShape.west, true),
        northEast: encodeCornerMask(brushShape.north, brushShape.northEast, true, brushShape.east),
        southWest: encodeCornerMask(brushShape.west, true, brushShape.southWest, brushShape.south),
        southEast: encodeCornerMask(true, brushShape.east, brushShape.south, brushShape.southEast),
      }, textureImage),
      applyCellTransform: true,
    };
  },
  resolveLiveRenderTile(map, atlasId, x, y, _cell, atlas) {
    const cornerMasks = squareCornerMasks(map, atlasId, x, y);
    if (!cornerMasks) {
      return null;
    }
    const textureImage = getPathTileTextureSync(atlasId, 'square');
    return {
      kind: 'image',
      image: composeSquarePathTileFromCornerMasks(atlasId, atlas.tileSize * 2, cornerMasks, textureImage),
      applyCellTransform: false,
    };
  },
};

const hexPathProfile: PathAutotileProfile = {
  id: 'hex-path-v1',
  getAffectedCoordinates(centerX, centerY) {
    return [
      { x: centerX, y: centerY },
      ...hexNeighborOffsets(centerX).map((offset) => ({ x: centerX + offset.x, y: centerY + offset.y })),
    ];
  },
  recommendAt(map, atlasId, x, y, overrides) {
    const mask = buildHexNeighborMask(map, atlasId, x, y, overrides);
    return mask === null ? null : resolveHexTileRecommendation(mask);
  },
  resolveFixedRenderTile(_map, _atlasId, _x, _y, cell, _atlas, fixedTileIndex) {
    const mask = resolveHexPathBrushMask(fixedTileIndex);
    if (mask === null) {
      return null;
    }
    return {
      kind: 'atlas-cell',
      tileIndex: mask,
      flipHorizontal: cell.flipHorizontal === true,
      flipVertical: cell.flipVertical === true,
      rotationSteps: cell.rotationSteps ?? 0,
    };
  },
  resolveLiveRenderTile(map, atlasId, x, y) {
    const mask = buildHexNeighborMask(map, atlasId, x, y);
    if (mask === null) {
      return null;
    }
    return {
      kind: 'atlas-cell',
      tileIndex: mask,
      flipHorizontal: false,
      flipVertical: false,
      rotationSteps: 0,
    };
  },
};

const trianglePathProfile: PathAutotileProfile = {
  id: 'triangle-path-v1',
  getAffectedCoordinates(centerX, centerY) {
    const coords: { x: number; y: number }[] = [];
    for (let y = centerY - 1; y <= centerY + 1; y += 1) {
      for (let x = centerX - 1; x <= centerX + 1; x += 1) {
        coords.push({ x, y });
      }
    }
    return coords;
  },
  recommendAt(map, atlasId, x, y, overrides) {
    const mask = buildTriangleNeighborMask(map, atlasId, x, y, overrides);
    return mask === null ? null : resolveTriangleTileRecommendation(mask);
  },
  resolveFixedRenderTile(_map, _atlasId, x, y, cell, _atlas, fixedTileIndex) {
    const brush = resolveTrianglePathBrush(fixedTileIndex);
    if (!brush) {
      return null;
    }
    if (brush.kind === 'variant') {
      const up = ((x + y) & 1) === 0;
      return {
        kind: 'image',
        image: composeTrianglePathTile(_atlasId, _atlas.tileSize, resolveTriangleVariantMask(brush.variant, up), up, brush.variant),
        applyCellTransform: true,
      };
    }
    const mask = resolveTrianglePathBrushMask(brush.mask);
    if (mask === null) {
      return null;
    }
    return {
      kind: 'atlas-cell',
      tileIndex: trianglePathAtlasTileIndex(mask, x, y),
      flipHorizontal: cell.flipHorizontal === true,
      flipVertical: cell.flipVertical === true,
      rotationSteps: cell.rotationSteps ?? 0,
    };
  },
  resolveLiveRenderTile(map, atlasId, x, y, _cell, atlas) {
    const mask = buildTriangleNeighborMask(map, atlasId, x, y);
    if (mask === null) {
      return null;
    }
    const up = ((x + y) & 1) === 0;
    const fillCorners = resolveTriangleHexagonFillCorners(map, atlasId, x, y, mask);
    if (fillCorners.length > 0) {
      return {
        kind: 'image',
        image: composeTrianglePathTile(atlasId, atlas.tileSize, mask, up, null, fillCorners),
        applyCellTransform: false,
      };
    }
    return {
      kind: 'atlas-cell',
      tileIndex: trianglePathAtlasTileIndex(mask, x, y),
      flipHorizontal: false,
      flipVertical: false,
      rotationSteps: 0,
    };
  },
};

function resolveProfile(map: MapDefinition, atlasId: PathTileAtlasId): PathAutotileProfile | null {
  const profile = getPathAutotileProfile(map, atlasId);
  if (profile === squarePathProfile.id) {
    return squarePathProfile;
  }
  if (profile === hexPathProfile.id) {
    return hexPathProfile;
  }
  if (profile === trianglePathProfile.id) {
    return trianglePathProfile;
  }
  return null;
}

export const profileAwarePathAutotiler: PathAutotiler = {
  id: 'profile-aware-v2',

  recommendAt(map, atlasId, x, y): PathTileRecommendation | null {
    return resolveProfile(map, atlasId)?.recommendAt(map, atlasId, x, y) ?? null;
  },

  recommendForNeighborStatus(signature: PathTileNeighborStatus): PathTileRecommendation {
    return squareRecommendForNeighborStatus(signature);
  },

  buildNeighborRetilePatch(
    map: MapDefinition,
    atlasId: PathTileAtlasId,
    centerX: number,
    centerY: number,
    centerCell: PathCell | null,
  ): NeighborRetilePatchEntry[] {
    if (!isWithinMap(map, centerX, centerY)) {
      return [];
    }
    return [{ x: centerX, y: centerY, cell: centerCell }];
  },

  buildNeighborFreezePatch(
    map: MapDefinition,
    atlasId: PathTileAtlasId,
    centerX: number,
    centerY: number,
  ): NeighborRetilePatchEntry[] {
    return [];
  },

  buildGhostBorderRetilePatch(
    _map: MapDefinition,
    _atlasId: PathTileAtlasId,
    _ghostX: number,
    _ghostY: number,
    _ghostActive: boolean,
  ): NeighborRetilePatchEntry[] {
    return [];
  },

  resolveRenderTile(
    map: MapDefinition,
    atlasId: PathTileAtlasId,
    x: number,
    y: number,
    cell: PathCell | null,
    atlas: PathTileAtlasDefinition,
  ): PathTileRenderTile | null {
    const profile = resolveProfile(map, atlasId);
    return profile
      ? resolveRenderTileWithProfile(profile, map, atlasId, x, y, cell, atlas)
      : null;
  },
};
